<template>
  <div class="tax-root">
    <div class="tax-section">
      <div class="tax-title">{{ $t('tool.TaxCalculator.type') }}</div>
      <el-radio-group v-model="incomeType" size="small" @change="calc">
        <el-radio-button label="salary">{{ $t('tool.TaxCalculator.typeSalary') }}</el-radio-button>
        <el-radio-button label="bonus">{{ $t('tool.TaxCalculator.typeBonus') }}</el-radio-button>
        <el-radio-button label="labor">{{ $t('tool.TaxCalculator.typeLabor') }}</el-radio-button>
      </el-radio-group>
    </div>

    <div class="tax-form">
      <!-- 工资薪金 -->
      <template v-if="incomeType === 'salary'">
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.salary') }}</label>
          <el-input v-model.number="sIncome" type="number" size="small" :placeholder="$t('tool.TaxCalculator.phSalary')" @input="calc" />
        </div>
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.socialInsurance') }}</label>
          <el-input v-model.number="sSocial" type="number" size="small" :placeholder="$t('tool.TaxCalculator.phSocial')" @input="calc" />
        </div>
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.deduction') }}</label>
          <el-input v-model.number="sExtra" type="number" size="small" :placeholder="$t('tool.TaxCalculator.phDeduction')" @input="calc" />
        </div>
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.threshold') }}</label>
          <el-input v-model.number="sThreshold" type="number" size="small" :min="0" :placeholder="$t('tool.TaxCalculator.phThreshold')" @input="calc" />
        </div>
      </template>

      <!-- 年终奖 -->
      <template v-if="incomeType === 'bonus'">
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.bonus') }}</label>
          <el-input v-model.number="bAmount" type="number" size="small" :placeholder="$t('tool.TaxCalculator.phBonus')" @input="calc" />
        </div>
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.bonusSalaryLabel') }}</label>
          <el-switch v-model="bOverThreshold" @change="calc" :active-text="$t('tool.TaxCalculator.yes')" :inactive-text="$t('tool.TaxCalculator.no')" />
        </div>
      </template>

      <!-- 劳务报酬 -->
      <template v-if="incomeType === 'labor'">
        <div class="tax-row">
          <label>{{ $t('tool.TaxCalculator.labor') }}</label>
          <el-input v-model.number="lIncome" type="number" size="small" :placeholder="$t('tool.TaxCalculator.phLabor')" @input="calc" />
        </div>
      </template>
    </div>

    <!-- 结果 -->
    <div class="tax-result" v-if="result">
      <div class="tax-cards">
        <div class="tax-card" v-if="result.taxable !== null">
          <div class="tax-card-val">{{ fmt(result.taxable) }}</div>
          <div class="tax-card-label">{{ $t('tool.TaxCalculator.taxable') }}</div>
        </div>
        <div class="tax-card tax-card-highlight">
          <div class="tax-card-val">{{ fmt(result.tax) }}</div>
          <div class="tax-card-label">{{ $t('tool.TaxCalculator.result') }}</div>
        </div>
        <div class="tax-card">
          <div class="tax-card-val">{{ fmt(result.afterTax) }}</div>
          <div class="tax-card-label">{{ $t('tool.TaxCalculator.afterTax') }}</div>
        </div>
      </div>
      <div class="tax-rate-info" v-if="result.rateInfo">{{ result.rateInfo }}</div>

      <!-- 税率表参考 -->
      <el-collapse style="margin-top:14px;">
        <el-collapse-item name="table">
          <template slot="title"><span class="tax-ref-title">{{ $t('tool.TaxCalculator.rateRefTitle') }}</span></template>
          <table class="tax-table">
            <thead><tr><th>{{ $t('tool.TaxCalculator.colLevel') }}</th><th>{{ $t('tool.TaxCalculator.colTaxableRange') }}</th><th>{{ $t('tool.TaxCalculator.colRate') }}</th><th>{{ $t('tool.TaxCalculator.colQuickDeduction') }}</th></tr></thead>
            <tbody>
              <tr v-for="r in taxBrackets" :key="r.level">
                <td>{{ r.level }}</td><td>{{ r.range }}</td><td>{{ r.rate }}%</td><td>{{ r.quick }}</td>
              </tr>
            </tbody>
          </table>
        </el-collapse-item>
      </el-collapse>
    </div>

    <div class="tax-empty" v-else>{{ $t('tool.TaxCalculator.emptyHint') }}</div>
  </div>
</template>

<script>
var TAX_BRACKETS_RAW = [
  { level: 1, rate: 3, quick: 0, max: 3000 },
  { level: 2, rate: 10, quick: 210, max: 12000 },
  { level: 3, rate: 20, quick: 1410, max: 25000 },
  { level: 4, rate: 25, quick: 2660, max: 35000 },
  { level: 5, rate: 30, quick: 4410, max: 55000 },
  { level: 6, rate: 35, quick: 7160, max: 80000 },
  { level: 7, rate: 45, quick: 15160, max: Infinity }
]

function fmt(n) {
  if (typeof n !== 'number' || isNaN(n)) return '0'
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function calcSalaryTax(taxable) {
  if (taxable <= 0) return { tax: 0, rate: 0, quick: 0 }
  for (var i = 0; i < TAX_BRACKETS_RAW.length; i++) {
    var b = TAX_BRACKETS_RAW[i]
    if (taxable <= b.max || i === TAX_BRACKETS.length - 1) {
      return { tax: Math.max(0, taxable * b.rate / 100 - b.quick), rate: b.rate, quick: b.quick }
    }
  }
}

export default {
  name: 'TaxCalculator',
  props: { config: { type: Object, default: function() { return {} } } },
  data: function() {
    return {
      incomeType: 'salary',
      sIncome: 20000, sSocial: 4000, sExtra: 3000, sThreshold: 5000,
      bAmount: 100000, bOverThreshold: true,
      lIncome: 50000,
      result: null
    }
  },
  computed: {
    taxBrackets: function() {
      var t = this, brackets = []
      for (var i = 0; i < TAX_BRACKETS_RAW.length; i++) {
        var b = TAX_BRACKETS_RAW[i], range
        if (i === 0) range = t.$t('tool.TaxCalculator.bracketUpTo', { n: '3,000' })
        else if (i === TAX_BRACKETS_RAW.length - 1) range = t.$t('tool.TaxCalculator.bracketOver', { n: '80,000' })
        else {
          var prev = TAX_BRACKETS_RAW[i-1]
          range = t.$t('tool.TaxCalculator.bracketRange', { from: fmt(prev.max), to: fmt(b.max) })
        }
        brackets.push({ level: b.level, range: range, rate: b.rate, quick: b.quick, max: b.max })
      }
      return brackets
    }
  },
  created: function() { this.calc() },
  methods: {
    fmt: fmt,
    calc: function() {
      var result = { tax: 0, afterTax: 0, taxable: null, rateInfo: '' }

      if (this.incomeType === 'salary') {
        var taxable = this.sIncome - this.sThreshold - this.sSocial - this.sExtra
        if (taxable <= 0) {
          result.taxable = 0; result.tax = 0; result.afterTax = this.sIncome - this.sSocial
          result.rateInfo = this.$t('tool.TaxCalculator.taxFree')
        } else {
          var r = calcSalaryTax(taxable)
          result.taxable = taxable; result.tax = r.tax
          result.afterTax = this.sIncome - this.sSocial - r.tax
          result.rateInfo = this.$t('tool.TaxCalculator.rateInfo', { rate: r.rate, quick: fmt(r.quick) })
        }
      } else if (this.incomeType === 'bonus') {
        var monthAvg = this.bAmount / 12
        var r2 = calcSalaryTax(monthAvg)
        result.tax = Math.max(0, this.bAmount * r2.rate / 100 - r2.quick)
        result.afterTax = this.bAmount - result.tax
        result.rateInfo = this.$t('tool.TaxCalculator.bonusInfo', { avg: fmt(monthAvg), rate: r2.rate, quick: fmt(r2.quick) })
      } else if (this.incomeType === 'labor') {
        var income = this.lIncome
        if (income <= 800) {
          result.taxable = 0; result.tax = 0
          result.rateInfo = this.$t('tool.TaxCalculator.laborFree')
        } else if (income <= 4000) {
          result.taxable = income - 800
          result.tax = result.taxable * 0.2
          result.rateInfo = this.$t('tool.TaxCalculator.laborLow')
        } else {
          var taxable2 = income * 0.8
          if (taxable2 <= 20000) {
            result.tax = taxable2 * 0.2
            result.rateInfo = this.$t('tool.TaxCalculator.laborMid20')
          } else if (taxable2 <= 50000) {
            result.tax = taxable2 * 0.3 - 2000
            result.rateInfo = this.$t('tool.TaxCalculator.laborMid30')
          } else {
            result.tax = taxable2 * 0.4 - 7000
            result.rateInfo = this.$t('tool.TaxCalculator.laborMid40')
          }
        }
        result.afterTax = income - result.tax
      }

      if (!result.taxable) result.taxable = null
      this.result = result
    },
    doAction: function(a) {
      if (a === 'clear') {
        this.sIncome = 0; this.sSocial = 0; this.sExtra = 0; this.sThreshold = 5000
        this.bAmount = 0; this.lIncome = 0
        this.result = null
      }
      if (a === 'copy' && this.result) {
        var text = this.$t('tool.TaxCalculator.copyTax', { tax: fmt(this.result.tax), afterTax: fmt(this.result.afterTax) })
        navigator.clipboard.writeText(text)
      }
    }
  }
}
</script>

<style>
.tax-root { width: 100%; }

.tax-section { margin-bottom: 16px; }
.tax-title { font-size: 14px; font-weight: 700; color: #303133; margin-bottom: 10px; }

.tax-form { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 20px; margin-bottom: 18px; }
.tax-row { display: flex; flex-direction: column; gap: 4px; }
.tax-row label { font-size: 13px; font-weight: 600; color: #606266; }

.tax-cards { display: flex; gap: 14px; }
.tax-card { flex: 1; background: #f5f7fa; border-radius: 10px; padding: 16px; text-align: center; }
.tax-card-highlight { background: #fef0f0; }
.tax-card-highlight .tax-card-val { color: #f56c6c; }
.tax-card-val { font-size: 20px; font-weight: 700; color: #4e7cff; word-break: break-all; }
.tax-card-label { font-size: 12px; color: #909399; margin-top: 4px; }

.tax-rate-info { margin-top: 12px; font-size: 13px; color: #8892a6; padding: 8px 14px; background: #fafbfc; border-radius: 6px; }

.tax-ref-title { font-size: 13px; font-weight: 600; color: #606266; }

.tax-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.tax-table th, .tax-table td { padding: 6px 12px; text-align: center; border-bottom: 1px solid #ebeef5; }
.tax-table th { background: #fafbfc; color: #909399; font-weight: 600; }
.tax-table td { color: #303133; }

.tax-empty { text-align: center; padding: 40px; color: #c0c4cc; font-size: 14px; }

@media (max-width: 640px) { .tax-form, .tax-cards { flex-direction: column; grid-template-columns: 1fr; } }
</style>
